import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { validateEmail } from "@/dentlab/src/hooks/useForm/form-field-validation-functions";
import axios from "axios";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { Label } from "@/dentlab/src/components/ui/label";
import { AuthBanner } from "@/components/src/auth-banner/auth-banner.component";
import { Shield } from "lucide-react";
import { environment } from "@/dentlab/src/lib/utils/environment";

export const VendorSigninPage: React.FC<{
	onLoginSuccess?: (data: any) => void;
}> = ({ onLoginSuccess }) => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const handleLoginComplete = (data: any) => {
		if (onLoginSuccess) {
			onLoginSuccess(data);
		} else {
			navigate("/");
		}
	};

	const [email, setEmail] = useState<string>("");
	const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
	const [otp, setOTP] = useState<string>("");
	const [loginMode, setLoginMode] = useState<"otp" | "password">("otp");
	const [password, setPassword] = useState<string>("");
	const [error, setError] = useState(false);
	const [loginError, setLoginError] = useState<{
		message: string;
		isCritical?: boolean;
	} | null>(null);

	useEffect(() => {
		if (searchParams.has("email")) {
			setEmail(searchParams.get("email") as string);
		}
	}, [searchParams]);

	const handleSendOTP = async () => {
		Logger.log("VendorSigninPage", "handleSendOTP", email);
		setLoginError(null);
		let data;
		let error;
		const { data: supabaseData, error: supabaseError } =
			await supabase.auth.signInWithOtp({
				email: email.toLowerCase(),
			});
		data = supabaseData;
		error = supabaseError;
		if (error) {
			try {
				const response = await axios({
					url: `${environment.VITE_PDF_MICROSERVICE_URL}/login/request-otp`,
					method: "POST",
					data: {
						email: email.toLowerCase(),
					},
				});
				data = response.data.data;
				error = response.data.error;
				if (!error) {
					setIsOTPSent(true);
					return;
				} else {
					setLoginError({
						message:
							"Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
						isCritical: false,
					});
				}
			} catch (error) {
				Logger.error(
					error,
					{ identifier: "otp" },
					{ data, error, email }
				);
				setLoginError({
					message:
						"Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
					isCritical: true,
				});
			}
		} else {
			setIsOTPSent(true);
		}
		Logger.log("VendorSigninPage", "handleSendOTP", data, error);
	};

	const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(event.target.value);
		if (loginError) {
			setLoginError(null);
		}
	};

	const handleOtpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setOTP(event.target.value);
		if (loginError) {
			setLoginError(null);
		}
	};

	const handlePasswordChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setPassword(event.target.value);
		if (loginError) {
			setLoginError(null);
		}
	};

	const handleSubmitOtp = async () => {
		Logger.log("VendorSigninPage", "handleSubmitOtp", email, otp);
		setLoginError(null);
		const token = otp;
		let data;
		let error;
		const { data: supabaseData, error: supabaseError } =
			await supabase.auth.verifyOtp({
				email,
				token,
				type: "email",
			});
		data = supabaseData;
		error = supabaseError;
		Logger.log("VendorSigninPage", "handleSubmitOtp", data, error);
		if (error) {
			try {
				const response = await axios({
					url: `${environment.VITE_PDF_MICROSERVICE_URL}/login/verify-otp`,
					method: "POST",
					data: {
						email: email.toLowerCase(),
						token: token,
					},
				});
				data = response.data.data;
				error = response.data.error;
				if (error) {
					setLoginError({
						message:
							"Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
						isCritical: false,
					});
				} else {
					handleLoginComplete(data);
				}
			} catch (error) {
				Logger.error(error, { identifier: "otp" }, { email, otp });
				setLoginError({
					message:
						"Fehler beim Einloggen. Bitte stellen Sie sicher, dass Sie die richtigen Anmeldedaten eingegeben haben.",
					isCritical: true,
				});
			}
		} else {
			handleLoginComplete(data);
		}
	};

	const handlePasswordLogin = async () => {
		Logger.log("VendorSigninPage", "handlePasswordLogin", email);
		setLoginError(null);

		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email: email.toLowerCase(),
				password: password,
			});

			if (error) {
				setLoginError({
					message:
						"Falsches Passwort oder E-Mail. Nutzen Sie alternativ das Einmalpasswort.",
					isCritical: false,
				});
			} else if (data) {
				handleLoginComplete(data);
			}
		} catch (error) {
			Logger.error(error, { identifier: "password-login" }, { email });
			setLoginError({
				message:
					"Fehler beim Einloggen. Bitte versuchen Sie es erneut.",
				isCritical: true,
			});
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const email = e.target.value;
		setError(!validateEmail(email));
	};

	return (
		<div className="flex min-h-screen flex-col">
			<AuthBanner />
			<div className="flex flex-1">
				<div className="w-full max-w-md lg:max-w-lg bg-gray-50 p-8 lg:p-12 flex items-center justify-center border-r border-gray-200 shadow-xl mr-8">
					<div className="w-full max-w-sm space-y-6">
						<div className="space-y-2">
							<Shield className="w-14 h-14 mb-4 -mt-12 text-gray-700" />
							<h1 className="text-3xl font-bold tracking-tight text-gray-900">
								{loginMode === "otp"
									? "Sicherer Zugriff mit Einmalpasswort"
									: "Anmelden mit Passwort"}
							</h1>
							<p className="text-gray-600">
								{loginMode === "otp"
									? "Geben Sie Ihre E-Mail ein, um ein Einmalpasswort zu erhalten. Nach dem Login bleiben Sie auf diesem Gerät angemeldet."
									: "Melden Sie sich mit Ihrer E-Mail und Ihrem Passwort an."}
							</p>
						</div>

						<div className="space-y-4">
							{loginMode === "otp" ? (
								isOTPSent ? (
									<>
										<div className="space-y-2">
											<Label htmlFor="otp">
												Zugangscode
											</Label>
											<Input
												id="otp"
												type="text"
												placeholder="Geben Sie Ihren Code ein"
												value={otp || ""}
												onChange={handleOtpChange}
												className="h-12"
												aria-label="Zugangscode eingeben"
												data-testid="vendor-signin-otp-input"
											/>
										</div>
										{loginError && (
											<div className="rounded-md bg-red-50 p-4">
												<p className="text-sm text-red-800">
													{loginError.message}
												</p>
												{loginError.isCritical && (
													<p className="text-sm text-red-800 mt-2">
														Für Unterstützung
														kontaktieren Sie uns
														unter:{" "}
														<strong>
															+41 31 561 93 27
														</strong>
													</p>
												)}
											</div>
										)}
										<Button
											onClick={handleSubmitOtp}
											size="lg"
											className="w-full h-12 text-base"
											aria-label="Zugangscode bestätigen"
											data-testid="vendor-signin-submit-otp-button"
										>
											Eingabe
										</Button>
									</>
								) : (
									<>
										<div className="space-y-2">
											<Input
												id="email"
												type="email"
												placeholder="E-Mail Adresse"
												value={email ?? ""}
												onChange={handleEmailChange}
												onBlur={handleBlur}
												className={`h-12 ${
													error
														? "border-red-500"
														: ""
												}`}
												required
												aria-label="E-Mail Adresse"
												data-testid="vendor-signin-email-input"
											/>
											{error && (
												<p className="text-sm text-red-500">
													Format stimmt nicht
												</p>
											)}
										</div>
										{loginError && (
											<div className="rounded-md bg-red-50 p-4">
												<p className="text-sm text-red-800">
													{loginError.message}
												</p>
												{loginError.isCritical && (
													<p className="text-sm text-red-800 mt-2">
														Für Unterstützung
														kontaktieren Sie uns
														unter:{" "}
														<strong>
															+41 31 561 93 27
														</strong>
													</p>
												)}
											</div>
										)}
										<Button
											onClick={handleSendOTP}
											size="lg"
											className="w-full h-12 text-base"
											aria-label="Zugangscode senden"
											data-testid="vendor-signin-send-otp-button"
										>
											Zugangscode senden
										</Button>
									</>
								)
							) : (
								<>
									<div className="space-y-2">
										<Input
											id="email"
											type="email"
											placeholder="E-Mail Adresse"
											value={email ?? ""}
											onChange={handleEmailChange}
											onBlur={handleBlur}
											className={`h-12 ${
												error ? "border-red-500" : ""
											}`}
											required
											aria-label="E-Mail Adresse"
											data-testid="vendor-signin-email-input"
										/>
										{error && (
											<p className="text-sm text-red-500">
												Format stimmt nicht
											</p>
										)}
									</div>
									<div className="space-y-2">
										<Input
											id="password"
											type="password"
											placeholder="Passwort"
											value={password}
											onChange={handlePasswordChange}
											className="h-12"
											required
											aria-label="Passwort"
											data-testid="vendor-signin-password-input"
										/>
									</div>
									{loginError && (
										<div className="rounded-md bg-red-50 p-4">
											<p className="text-sm text-red-800">
												{loginError.message}
											</p>
											{loginError.isCritical && (
												<p className="text-sm text-red-800 mt-2">
													Für Unterstützung
													kontaktieren Sie uns unter:{" "}
													<strong>
														+41 31 561 93 27
													</strong>
												</p>
											)}
										</div>
									)}
									<Button
										onClick={handlePasswordLogin}
										size="lg"
										className="w-full h-12 text-base"
										aria-label="Anmelden"
										data-testid="vendor-signin-password-submit-button"
									>
										Anmelden
									</Button>
								</>
							)}
						</div>

						<div className="text-center">
							{loginMode === "otp" && !isOTPSent ? (
								<button
									type="button"
									onClick={() => setLoginMode("password")}
									className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
								>
									Schon registriert? Zum Passwort-Login
								</button>
							) : loginMode === "password" ? (
								<button
									type="button"
									onClick={() => {
										setLoginMode("otp");
										setPassword("");
										setLoginError(null);
									}}
									className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
								>
									Zurück zum Einmalpasswort-Login
								</button>
							) : null}
						</div>
					</div>
				</div>

				<div className="flex-1 bg-white flex items-center justify-center p-8 lg:p-12">
					<div className="max-w-lg space-y-8">
						<div className="space-y-4">
							<h2 className="text-4xl font-bold tracking-tight text-gray-900">
								Austausch mit Laboren und Praxen
							</h2>
							<p className="text-lg text-gray-600">
								dentLAB ist Ihre sichere Plattform für die
								Zusammenarbeit mit Ihren Zahnärzten und anderen
								Laboren.
							</p>
						</div>
						<div className="space-y-4">
							<div className="flex items-start space-x-3">
								<svg
									className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<p className="text-gray-700">
									<b>Eingehende Aufträge</b> direkt empfangen
									und verwalten
								</p>
							</div>

							<div className="flex items-start space-x-3">
								<svg
									className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<p className="text-gray-700">
									<b>Sichere Kommunikation</b> und
									Dateiaustausch zu jedem Auftrag
								</p>
							</div>

							<div className="flex items-start space-x-3">
								<svg
									className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<p className="text-gray-700">
									<b>Übersichtliche Auftragsverwaltung</b> mit
									allen relevanten Informationen
								</p>
							</div>
							<p className="text-md pt-4 text-slate-700">
								Jetzt{" "}
								<a
									href="https://calendly.com/simon-hans-dentdesk/30-minuten-schulung-feedback-gesprach"
									target="_blank"
									rel="noopener noreferrer"
									className="text-slate-700 underline hover:text-blue-600"
								>
									kostenlose Online-Schulung
								</a>{" "}
								für Sie oder Ihr Team buchen.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
